import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { FlightSearchParams, PassengerInfo } from '~/types';
import {
  getPassengerDobBounds,
  PassengerValidationMessages,
} from '~/utils/flight-itinerary-validation';

import FlightItinerarySummaryCard from './FlightItinerarySummaryCard';

type Props = {
  searchParams: FlightSearchParams;
  customerEmail: string;
  passengers: PassengerInfo[];
  customerEmailValidationMessages?: string[];
  passengerValidationMessages?: PassengerValidationMessages;
  onCustomerEmailChange: (value: string) => void;
  onPassengerChange: (
    passengerId: string,
    field: keyof PassengerInfo,
    value: string | Date | null,
  ) => void;
  onNext?: () => void;
  showContinue?: boolean;
  onEditTrip: () => void;
};

const getHelperText = (messages?: string[]) => messages?.join(' ') || undefined;
const cardStyles = {
  p: { xs: 2, sm: 2.5 },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
};
const iconStyles = {
  color: '#0f4e92',
  bgcolor: '#edf4ff',
  p: 0.75,
  borderRadius: 1.5,
  fontSize: 34,
};

function FlightItineraryPassengerDetailsStep({
  customerEmail,
  customerEmailValidationMessages = [],
  onCustomerEmailChange,
  onEditTrip,
  onNext,
  onPassengerChange,
  passengers,
  passengerValidationMessages = {},
  searchParams,
  showContinue = true,
}: Props) {
  return (
    <Box
      sx={{
        '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
        '& .MuiFormHelperText-root': { mx: 0, mt: 0.75 },
      }}
    >
      <FlightItinerarySummaryCard onEdit={onEditTrip} searchParams={searchParams} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            aria-label="Customer contact details"
            component="section"
            elevation={0}
            sx={cardStyles}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr' },
                gap: 2.5,
                alignItems: 'start',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                <MailOutlineIcon sx={iconStyles} />
                <Box>
                  <Typography component="h2" sx={{ fontWeight: 700 }} variant="subtitle1">
                    Contact details
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Where should we send your itinerary?
                  </Typography>
                </Box>
              </Box>
              <TextField
                autoComplete="email"
                error={customerEmailValidationMessages.length > 0}
                fullWidth
                helperText={
                  getHelperText(customerEmailValidationMessages) ||
                  'We will send your itinerary to this email.'
                }
                label="Email"
                onChange={event => onCustomerEmailChange(event.target.value)}
                required
                type="email"
                value={customerEmail}
              />
            </Box>
          </Paper>
          <Typography component="h2" sx={{ mt: 3, fontWeight: 700 }} variant="subtitle1">
            Passenger details
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
            Enter each passenger’s name exactly as it appears on their passport.
          </Typography>
        </Grid>

        {passengers.map(passenger => {
          const fieldMessages = passengerValidationMessages[passenger.id] || {};
          const { helperText, maxDate, minDate } = getPassengerDobBounds(passenger.passengerType);
          const dobMinDate = minDate ? dayjs(minDate) : undefined;
          const dobMaxDate = maxDate ? dayjs(maxDate) : undefined;

          return (
            <Grid key={passenger.id} item xs={12}>
              <Paper
                aria-label={`Passenger ${passenger.id}`}
                component="section"
                elevation={0}
                sx={cardStyles}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                  <PersonOutlineIcon sx={iconStyles} />
                  <Typography component="h3" sx={{ fontWeight: 700 }} variant="subtitle1">
                    Passenger {passenger.id}
                  </Typography>
                  <Typography
                    sx={{
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontWeight: 600,
                    }}
                    variant="caption"
                  >
                    {passenger.passengerType}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" error={!!fieldMessages.gender?.length}>
                      <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
                        Gender
                      </FormLabel>
                      <RadioGroup
                        aria-label={`Passenger ${passenger.id} gender`}
                        onChange={event => {
                          onPassengerChange(passenger.id, 'gender', event.target.value);
                        }}
                        row
                        sx={{
                          gap: 1,
                          '& .MuiFormControlLabel-root': {
                            m: 0,
                            pr: 1.5,
                            minHeight: 44,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                          },
                          '& .MuiFormControlLabel-root:has(.Mui-checked)': {
                            borderColor: '#0f4e92',
                            bgcolor: '#edf4ff',
                          },
                          '& .MuiFormControlLabel-label': { fontSize: 14 },
                        }}
                        value={passenger.gender}
                      >
                        <FormControlLabel control={<Radio />} label="Male" value="MALE" />
                        <FormControlLabel control={<Radio />} label="Female" value="FEMALE" />
                      </RadioGroup>
                      {fieldMessages.gender?.length ? (
                        <Typography color="error" variant="caption">
                          {getHelperText(fieldMessages.gender)}
                        </Typography>
                      ) : null}
                    </FormControl>
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      error={!!fieldMessages.firstName?.length}
                      fullWidth
                      helperText={getHelperText(fieldMessages.firstName)}
                      label="First Name"
                      onChange={event => {
                        onPassengerChange(passenger.id, 'firstName', event.target.value);
                      }}
                      value={passenger.firstName}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      error={!!fieldMessages.lastName?.length}
                      fullWidth
                      helperText={getHelperText(fieldMessages.lastName)}
                      label="Surname"
                      onChange={event => {
                        onPassengerChange(passenger.id, 'lastName', event.target.value);
                      }}
                      value={passenger.lastName}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <DatePicker
                      label="Date of Birth"
                      maxDate={dobMaxDate}
                      minDate={dobMinDate}
                      onChange={value => {
                        onPassengerChange(
                          passenger.id,
                          'dateOfBirth',
                          value ? value.toDate() : null,
                        );
                      }}
                      shouldDisableDate={date => {
                        return !!(
                          (dobMinDate && date.isBefore(dobMinDate, 'day')) ||
                          (dobMaxDate && date.isAfter(dobMaxDate, 'day'))
                        );
                      }}
                      slotProps={{
                        textField: {
                          error: !!fieldMessages.dateOfBirth?.length,
                          helperText: getHelperText(fieldMessages.dateOfBirth) || helperText,
                        },
                      }}
                      sx={{ width: '100%' }}
                      value={passenger.dateOfBirth ? dayjs(passenger.dateOfBirth) : null}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
        {showContinue && onNext ? (
          <Grid item xs={12}>
            <Button onClick={onNext} variant="contained">
              Continue to review
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

export default FlightItineraryPassengerDetailsStep;
